import { DateTime } from "luxon";

import PopOutBox from "../shared/PopOutBox";
import classNames from "../../utils/classNames";
import SelectInput from "../shared/Inputs/SelectInput";
import { TextInput } from "../shared/Inputs/TextInput";
import { NumberInput } from "../shared/Inputs/NumberInput";
import { DateTimeInput } from "../shared/Inputs/DateTimeInput";
import AiConditionGenerator from "./AiConditionGenerator";
import { EbayCategoryDropdown } from "./EbayCategoryDropdown";
import { EbayConditionDropdown } from "./EbayConditionDropdown";
import { EbayStoreCategoryDropdown } from "./EbayStoreCategoryDropdown";
import { FormatType, ListingDuration } from "../../models/EbayOffer";
import { IPart } from "../../models/Part";

export const formatOptions = [
  { id: FormatType.Auction, name: "Auction" },
  { id: FormatType.FixedPrice, name: "Buy It Now" },
];

export const durationOptions = [
  { id: ListingDuration.Days1, name: "1 Day" },
  { id: ListingDuration.Days3, name: "3 Days" },
  { id: ListingDuration.Days5, name: "5 Days" },
  { id: ListingDuration.Days7, name: "7 Days" },
  { id: ListingDuration.Days10, name: "10 Days" },
  { id: ListingDuration.Days21, name: "21 Days" },
  { id: ListingDuration.Days30, name: "30 Days" },
];

type EditListingCoreFieldsProps = {
  part: IPart;
  register: any;
  getValues: any;
  setValue: any;
  watch: any;
  handleSetFormat: (_: string, value: any) => void;
  defaultPartCategory: any;
  policies: any;
  locations: any;
  bestOfferValue: boolean | null;
  isPublished: boolean;
};

export default function EditListingCoreFields({
  part,
  register,
  getValues,
  setValue,
  watch,
  handleSetFormat,
  defaultPartCategory,
  policies,
  locations,
  bestOfferValue,
  isPublished,
}: EditListingCoreFieldsProps) {
  return (
    <>
      <SelectInput
        label={"Listing Format"}
        register={register}
        registerName={"format"}
        options={formatOptions}
        setValue={handleSetFormat}
        watch={watch}
      />

      {getValues("format") === FormatType.Auction ? (
        <SelectInput
          label={"Duration"}
          register={register}
          registerName={"duration"}
          options={durationOptions}
          setValue={setValue}
          watch={watch}
        />
      ) : (
        <div className="sm:col-span-1 h-16">{/* Spacer */}</div>
      )}

      <div className="sm:col-span-1 flex gap-x-2">
        <div className="flex items-center">
          <input
            id="listNow"
            {...register("listNow")}
            type="checkbox"
            className="mr-2 h-5 w-5"
            disabled={part.soldDate !== null}
          />
          <label htmlFor="listNow">List Now</label>
        </div>

        {!watch("listNow") && (
          <div className="flex-1">
            <DateTimeInput
              label={"Listing Start"}
              register={register}
              registerName={"listingStarted"}
              min={DateTime.now().toFormat("yyyy-MM-dd'T'HH:mm")}
            />
          </div>
        )}
      </div>

      <EbayCategoryDropdown
        categoryId={part.offers.length > 0 ? part.offers[0]?.categoryId : defaultPartCategory.data?.data?.ebayCategoryId}
        register={register}
        registerName={"categoryId"}
        setValue={setValue}
        watch={watch}
      />

      <EbayStoreCategoryDropdown
        categoryId={part.offers.length > 0 ? part.offers[0]?.storeCategoryId : defaultPartCategory.data?.data?.ebayStoreCategoryId}
        register={register}
        registerName={"storeCategoryId"}
        setValue={setValue}
        watch={watch}
      />

      <div>
        {/* spacer */}
      </div>

      {getValues("format") === FormatType.Auction ? (
        <>
          <NumberInput
            label={"Starting Price (£)"}
            register={register}
            registerName={"auctionStartPrice"}
          />
          <NumberInput
            label={"Reserve Price (£)"}
            register={register}
            registerName={"auctionReservePrice"}
          />
        </>
      ) : (
        <>
          <NumberInput
            label={"Buy It Now Price (£)"}
            register={register}
            registerName={"buyItNowPrice"}
          />
          {bestOfferValue ? (
            <NumberInput
              label={"Best Offer Auto Accept Price (£)"}
              register={register}
              registerName={"autoAcceptPrice"}
            />
          ) : (
            <div className="sm:col-span-1 h-16">{/* Spacer */}</div>
          )}
        </>
      )}

      {getValues("format") === FormatType.FixedPrice && (
        <>
          <div className="sm:col-span-1 mt-4 flex gap-x-2">
            <input
              id="enableBestOffer"
              {...register("enableBestOffer")}
              type="checkbox"
              className={classNames((part.soldDate !== null || isPublished) && "text-gray-400", "h-5 w-5")}
              disabled={part.soldDate !== null || isPublished}
            />
            <label htmlFor="enableBestOffer">Allow Best Offer?</label>
            {isPublished && (
              <PopOutBox classes="w-96 z-30">
                <p>Ebay does not allow the Best Offer setting to be changed after a listing has been published.</p>
                <p>If you want to change this setting, you will need to withdraw the listing and create a new one.</p>
              </PopOutBox>
            )}
          </div>

          <div className="sm:col-span-1 h-16">{/* Spacer */}</div>
        </>
      )}

      <SelectInput
        label={"Shipping Policy*"}
        register={register}
        registerName={"fulfillmentPolicyId"}
        options={policies.data?.data.fulfilmentPolicies}
        setValue={setValue}
        watch={watch}
      />
      <SelectInput
        label={"Payment Policy*"}
        register={register}
        registerName={"paymentPolicyId"}
        options={policies.data?.data.paymentPolicies}
        setValue={setValue}
        watch={watch}
      />
      <SelectInput
        label={"Returns Policy*"}
        register={register}
        registerName={"returnPolicyId"}
        options={policies.data?.data.returnPolicies}
        setValue={setValue}
        watch={watch}
      />

      <SelectInput
        label={"Location of Part*"}
        register={register}
        registerName={"locationKey"}
        options={locations.data?.data.locations}
        setValue={setValue}
        watch={watch}
      />

      {getValues("categoryId") ? (
        <EbayConditionDropdown
          categoryId={getValues("categoryId")}
          register={register}
          registerName={"condition"}
          setValue={setValue}
          watch={watch}
        />
      ) : (
        <div className="flex flex-col">
          <label className="mb-2 text-sm font-medium text-gray-700">
            Condition*
          </label>
          <select className="text-sm bg-gray-400" value={1} disabled >
            <option value={1} disabled>Pick a Category</option>
          </select>
        </div>
      )}

      {watch("condition") < 2000 ? null :
        <div className="flex flex-col">
          <TextInput
            register={register}
            registerName={"conditionDescription"}
            label={"Condtion Description*"}
            richText
            rows={1}
          />
          <div className="mt-1.5">
            {/* Not gated on isPublished: unlike Best Offer, eBay lets the condition description
                be revised on a live listing, and the field above stays editable. */}
            <AiConditionGenerator
              part={part}
              onApply={(conditionDescription) =>
                setValue("conditionDescription", conditionDescription, { shouldDirty: true })
              }
            />
          </div>
        </div>
      }
    </>
  );
}
